"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Clock, Search, Tag, X } from "lucide-react";
import { AnimatedSearchBar } from "@/components/feed/animated-search-bar";
import { HeaderActions } from "@/components/feed/header-actions";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useSearchHistory } from "@/hooks/use-search-history";
import { useSearchSuggestions } from "@/hooks/use-search-suggestions";
import {
  PROFILE_HEADER_VISIBILITY_EVENT,
  PROFILE_STICKY_COMPACT_EVENT,
} from "@/lib/profile-header-events";
import { HUB_HEADER_COMPACT_EVENT } from "@/lib/hub-header-events";
import {
  markFeedSearchHeaderPortalHostsHydrated,
  resetFeedSearchHeaderPortalHostsHydrated,
} from "@/lib/feed-portal-hosts-hydrated";
import {
  HUB_HEADER_PORTAL_ATTR,
  PROFILE_HEADER_PORTAL_ATTR,
} from "@/lib/feed-header-portals";
import { HUB_OVERLAY_BACKDROP, HUB_OVERLAY_MASK } from "@/components/hub/hub-overlay-backdrop";
import { ROUTE_GUTTER_CLASS } from "@/lib/route-layout";
import { useTranslations } from "next-intl";

type FeedSearchHeaderProps = {
  currentUserId?: string;
};

export default function FeedSearchHeader({ currentUserId }: FeedSearchHeaderProps = {}) {
  const t = useTranslations("search");
  const tFeed = useTranslations("feedTabs");
  const [isSearchMode, setIsSearchMode] = useState(false);
  // Desktop persistent search field (mirrors the feed header). Mobile keeps the
  // collapsible AnimatedSearchBar.
  const [brandingSearchValue, setBrandingSearchValue] = useState("");
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFeedPage = pathname === "/feed" || pathname?.startsWith("/feed/");
  const isSearchPage = pathname === "/search";
  const headerRef = useRef<HTMLElement | null>(null);
  const subheaderRef = useRef<HTMLDivElement | null>(null);
  const isMdUp = useMediaQuery("(min-width: 768px)");
  const [searchValue, setSearchValue] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [collapseBrandingRow, setCollapseBrandingRow] = useState(false);
  const [profileHeaderVisible, setProfileHeaderVisible] = useState(false);
  const [profileStickyCompact, setProfileStickyCompact] = useState(false);
  const [hubHeaderCompact, setHubHeaderCompact] = useState(false);
  const idleTimerRef = useRef<number | null>(null);
  const { searchHistory, addToHistory, removeFromHistory } = useSearchHistory();
  const desktopHistory = useMemo(() => searchHistory.slice(0, 6), [searchHistory]);
  // Taxonomy autocomplete for the desktop search dropdown.
  const { suggestions: searchSuggestions } = useSearchSuggestions(searchValue, isMdUp);
  const trimmedSearch = searchValue.trim();
  const desktopSuggestionsOpen =
    isMdUp && showHistory && trimmedSearch.length >= 2 && searchSuggestions.length > 0;
  const desktopHistoryOpen =
    isMdUp && showHistory && trimmedSearch.length < 2 && desktopHistory.length > 0;

  const isProfilePage = useMemo(() => {
    if (!pathname) return false;
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length !== 1) return false;
    const reserved = new Set([
      "feed", "search", "saved", "offers", "my-listings", "orders-placed", "order-management",
      "orders", "profile", "settings", "shipping-settings", "terms", "privacy",
      "contact", "mission", "changelog", "poll", "contest", "contest-results",
      "analytics", "label-creation", "admin", "chat", "api",
    ]);
    return !reserved.has(parts[0]);
  }, [pathname]);

  // Edge-to-edge px-4 gutters, NOT the centered/capped ROUTE_HEADER_SHELL_CLASS
  // (mx-auto + max-w-(--route-max-width)) — the top header bar itself must look
  // identical to the feed's FeedTopBar on every page (same fixed side padding at
  // any viewport width), unlike page CONTENT columns which legitimately center
  // and cap. Mismatched classes here were the visible "padding jumps when I
  // navigate from /feed to /chat" bug.
  const brandingRowShellClass = ROUTE_GUTTER_CLASS;

  const disableBrandingRow = useMemo(() => {
    if (isMdUp && isProfilePage && profileHeaderVisible) return true;
    if (isMdUp) return false;
    if (!pathname) return false;
    if (pathname === "/saved") return true;
    if (pathname === "/offers") return true;
    if (pathname === "/shipping-settings") return true;
    if (pathname === "/profile/edit") return true;
    if (pathname === "/contact") return true;
    if (pathname === "/changelog") return true;
    if (pathname === "/analytics") return true;
    if (pathname === "/my-listings") return true;
    if (pathname === "/order-management") return true;
    if (pathname === "/orders-placed") return true;

    return false;
  }, [isMdUp, pathname, isProfilePage, profileHeaderVisible]);

  const isHubPage = useMemo(() => {
    if (!pathname) return false;
    return (
      pathname === "/offers" ||
      pathname === "/orders-placed" ||
      pathname === "/order-management" ||
      pathname === "/my-listings"
    );
  }, [pathname]);

  useEffect(() => {
    const handler = (e: CustomEvent<{ visible: boolean }>) => {
      setProfileHeaderVisible(e.detail?.visible ?? false);
    };
    window.addEventListener(PROFILE_HEADER_VISIBILITY_EVENT, handler as EventListener);
    return () => window.removeEventListener(PROFILE_HEADER_VISIBILITY_EVENT, handler as EventListener);
  }, []);

  useLayoutEffect(() => {
    const handler = (e: CustomEvent<{ visible: boolean }>) => {
      setProfileStickyCompact(e.detail?.visible ?? false);
    };
    window.addEventListener(PROFILE_STICKY_COMPACT_EVENT, handler as EventListener);
    return () => window.removeEventListener(PROFILE_STICKY_COMPACT_EVENT, handler as EventListener);
  }, []);

  useEffect(() => {
    const handler = (e: CustomEvent<{ visible: boolean }>) => {
      setHubHeaderCompact(e.detail?.visible ?? false);
    };
    window.addEventListener(HUB_HEADER_COMPACT_EVENT, handler as EventListener);
    return () => window.removeEventListener(HUB_HEADER_COMPACT_EVENT, handler as EventListener);
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    let raf = 0;
    // Ownership flag: only clear --subheader-height if WE set it. Page-owned
    // bars (saved, my-listings) write this same global var; clobbering it every
    // rAF (removeProperty when we have no subheader) was the scroll jitter.
    let weSetSubheader = false;

    const update = () => {
      raf = 0;
      const brandingEl = headerRef.current?.querySelector(
        "[data-header-branding-row]"
      ) as HTMLElement | null;
      const stickyEl = headerRef.current?.querySelector(
        "[data-profile-sticky-header]"
      ) as HTMLElement | null;
      const stickyVisible =
        !!stickyEl &&
        isProfilePage &&
        stickyEl.getBoundingClientRect().height > 0 &&
        parseFloat(getComputedStyle(stickyEl).opacity) > 0.02;

      setProfileStickyCompact((prev) => (prev === stickyVisible ? prev : stickyVisible));

      let brandingH =
        disableBrandingRow || !brandingEl ? 0 : Math.ceil(brandingEl.getBoundingClientRect().height);
      if (stickyVisible) {
        brandingH = 0;
      }

      const subheaderEl = subheaderRef.current;
      let measuredSub = 0;
      if (isProfilePage) {
        measuredSub = stickyEl ? Math.ceil(stickyEl.getBoundingClientRect().height) : 0;
      } else if (subheaderEl) {
        measuredSub = Math.ceil(subheaderEl.getBoundingClientRect().height);
      }
      const minSubReserve = pathname === "/search" && isMdUp ? 52 : 0;
      const subheaderH = Math.max(measuredSub, minSubReserve);

      const totalChrome = headerRef.current
        ? Math.ceil(headerRef.current.getBoundingClientRect().height)
        : brandingH + subheaderH;

      root.style.setProperty("--header-height", `${brandingH}px`);
      root.style.setProperty("--fixed-chrome-height", `${totalChrome}px`);
      if (subheaderH > 0) {
        root.style.setProperty("--subheader-height", `${subheaderH}px`);
        weSetSubheader = true;
      } else if (weSetSubheader) {
        // Only clear the var we own — never a page-owned bar's value.
        root.style.removeProperty("--subheader-height");
        weSetSubheader = false;
      }
    };

    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    update();

    const ro = new ResizeObserver(schedule);
    if (headerRef.current) ro.observe(headerRef.current);
    if (subheaderRef.current) ro.observe(subheaderRef.current);

    window.addEventListener("resize", schedule, { passive: true } as any);
    window.addEventListener("scroll", schedule, { passive: true } as any);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", schedule as any);
      window.removeEventListener("scroll", schedule as any);
      if (raf) cancelAnimationFrame(raf);
      root.style.removeProperty("--fixed-chrome-height");
      if (weSetSubheader) root.style.removeProperty("--subheader-height");
    };
  }, [disableBrandingRow, isMdUp, isProfilePage, pathname]);

  const collapseBrandingEnabled = useMemo(() => {
    if (!pathname) return false;
    if (pathname === "/saved") return true;
    if (pathname === "/offers") return true;
    if (pathname === "/my-listings") return true;
    if (pathname.startsWith("/listings/") && pathname.split("/").filter(Boolean).length === 2) return true;

    const parts = pathname.split("/").filter(Boolean);
    if (parts.length !== 1) return false;

    const slug = parts[0];
    const reserved = new Set([
      "feed",
      "search",
      "saved",
      "offers",
      "my-listings",
      "orders-placed",
      "order-management",
      "orders",
      "profile",
      "settings",
      "shipping-settings",
      "terms",
      "privacy",
      "contact",
      "mission",
      "changelog",
      "poll",
      "contest",
      "contest-results",
      "analytics",
      "label-creation",
      "admin",
      "chat",
      "api",
    ]);
    if (reserved.has(slug)) return false;
    return true;
  }, [pathname]);

  useEffect(() => {
    if (disableBrandingRow) {
      setCollapseBrandingRow(false);
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }
    if (isMdUp) {
      setCollapseBrandingRow(false);
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }
    if (!collapseBrandingEnabled) {
      setCollapseBrandingRow(false);
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }

    if (profileStickyCompact && isProfilePage) {
      setCollapseBrandingRow(true);
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }

    let raf = 0;
    const threshold = 10;
    const compute = () => {
      raf = 0;
      const y = window.scrollY || 0;
      if (y <= threshold) {
        setCollapseBrandingRow(false);
        return;
      }
      setCollapseBrandingRow(true);
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = window.setTimeout(() => {
        idleTimerRef.current = null;
        setCollapseBrandingRow(false);
      }, 220);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [collapseBrandingEnabled, disableBrandingRow, isMdUp, isProfilePage, profileStickyCompact]);

  // Spec §E 1.6b: search is a surface (route /search) on every breakpoint —
  // this used to branch to a local FeedSearchTabSlider overlay on mobile
  // (unless already on /search), the second of two independent mobile-search
  // entry points that both fed the §B.4 reset bug (the other was
  // feed-page-client-mobile.tsx's FeedTopBar). Now identical to the desktop
  // path: always route.
  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    router.push(`/search?query=${encodeURIComponent(trimmed)}`);
  };

  const submitDesktopSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    addToHistory(trimmed);
    setShowHistory(false);
    handleSearch(trimmed);
  };

  useEffect(() => {
    if (!isSearchPage) return;
    const next = (searchParams.get("query") || searchParams.get("q") || "").trim();
    setSearchValue(next);
    if (next) {
      addToHistory(next);
    }
  }, [isSearchPage, searchParams]);

  useLayoutEffect(() => {
    return;
  }, []);

  useEffect(() => {
    const headerSuppressed = isSearchPage && !isMdUp;
    if (headerSuppressed) {
      resetFeedSearchHeaderPortalHostsHydrated();
      return undefined;
    }
    markFeedSearchHeaderPortalHostsHydrated();
    return () => resetFeedSearchHeaderPortalHostsHydrated();
  }, [isSearchPage, isMdUp]);

  return (
    <>
      {!(isSearchPage && !isMdUp) && (
      <header
        ref={headerRef}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 safe-area-top",
          !isFeedPage && "pointer-events-none isolate"
        )}
        data-feed-top-bar
        data-global-header
        {...(isMdUp && isProfilePage && profileHeaderVisible && { "data-profile-header-integrated": "true" })}
        {...(profileStickyCompact && isProfilePage && { "data-profile-sticky-integrated": "true" })}
        {...(hubHeaderCompact && isHubPage && { "data-hub-header-integrated": "true" })}
      >
        {isFeedPage ? (
          <div className="absolute inset-0 bg-linear-to-b from-black/60 via-black/30 to-transparent pointer-events-none md:hidden" />
        ) : (
          <div
            aria-hidden
            style={HUB_OVERLAY_MASK}
            className={HUB_OVERLAY_BACKDROP}
          />
        )}
        <div className={cn("relative", !isFeedPage && "z-1 pointer-events-auto")}>
          {!disableBrandingRow ? (
            <div
              data-header-branding-row
              className={cn(
                "transition-all duration-200 overflow-hidden max-h-14",
                (collapseBrandingRow ||
                  (hubHeaderCompact && isHubPage && !isMdUp) ||
                  (profileStickyCompact && isProfilePage)) &&
                  cn(
                    "max-h-0 opacity-0 pointer-events-none",
                    !(profileStickyCompact && isProfilePage) && "-translate-y-2",
                  ),
              )}
            >
              {isSearchPage ? (
              <>
                <div className={cn("relative hidden h-14 items-center gap-3 md:flex md:gap-4", brandingRowShellClass)}>
                  <Link
                    href="/feed"
                    className="shrink-0 text-3xl font-semibold tracking-tight text-foreground whitespace-nowrap lg:text-4xl"
                  >
                    Kraahl
                  </Link>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      submitDesktopSearch(searchValue);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-3"
                  >
                    <div className="relative min-w-0 flex-1">
                      <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <input
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        placeholder=""
                        aria-label={t("searchLabel")}
                        className={cn(
                          "h-10 w-full rounded-full border border-border/60 bg-muted pl-12 focus:outline-none focus:ring-2 focus:ring-primary/40",
                          searchValue.trim().length >= 2 ? "pr-10" : "pr-4"
                        )}
                        onFocus={() => {
                          if (!isMdUp) return;
                          setShowHistory(true);
                        }}
                        onBlur={() => {
                          if (!isMdUp) return;
                          window.setTimeout(() => setShowHistory(false), 180);
                        }}
                      />
                      {searchValue.trim().length >= 2 ? (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchValue("");
                            router.push("/search");
                            if (isMdUp && desktopHistory.length > 0) {
                              setShowHistory(true);
                            }
                          }}
                          aria-label={t("clearSearch")}
                          onMouseDown={(e) => e.preventDefault()}
                          className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full hover:bg-background/80"
                        >
                          <X className="h-4 w-4 text-muted-foreground" />
                        </button>
                      ) : null}

                      {desktopSuggestionsOpen ? (
                        <div
                          className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-xl border border-border/60 bg-card/95 shadow-lg backdrop-blur-md"
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <ul className="max-h-[min(50vh,20rem)] overflow-y-auto px-1.5 py-1.5">
                            {searchSuggestions.map((suggestion) => (
                              <li key={`${suggestion.type}:${suggestion.label}`} className="py-1.5">
                                <button
                                  type="button"
                                  className="flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-xl border border-transparent px-2.5 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/80 active:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card dark:hover:bg-white/10"
                                  onClick={() => {
                                    setSearchValue(suggestion.label);
                                    submitDesktopSearch(suggestion.label);
                                  }}
                                >
                                  <Tag className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                  <span className="min-w-0 truncate">{suggestion.label}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : desktopHistoryOpen ? (
                        <div
                          className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-xl border border-border/60 bg-card/95 shadow-lg backdrop-blur-md"
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <div className="border-b border-border/60 px-3 py-2 text-xs font-semibold text-muted-foreground">
                            {t("recentSearches")}
                          </div>
                          <ul className="max-h-[min(50vh,20rem)] overflow-y-auto px-1.5 py-1.5">
                            {desktopHistory.map((term) => (
                              <li key={term} className="border-b border-border/60 py-1.5 last:border-b-0">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-xl border border-transparent px-2.5 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/80 active:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card dark:hover:bg-white/10"
                                    onClick={() => {
                                      setSearchValue(term);
                                      submitDesktopSearch(term);
                                    }}
                                  >
                                    <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                    <span className="min-w-0 truncate">{term}</span>
                                  </button>
                                  <button
                                    type="button"
                                    aria-label={t("removeSearch")}
                                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-transparent text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground active:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card dark:hover:bg-white/10"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      removeFromHistory(term);
                                    }}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="submit"
                      disabled={!searchValue.trim()}
                      aria-label={t("submitSearch")}
                      className="inline-flex h-10 min-w-10 shrink-0 items-center justify-center rounded-full border border-black/10 bg-white px-6 font-semibold text-black hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t("searchLabel")}
                    </button>
                  </form>
                </div>
              </>
              ) : (
              <div className={cn("relative flex items-center gap-2 h-14 md:gap-4", brandingRowShellClass)}>
                <div
                  className={cn(
                    "shrink-0 transition-all duration-200",
                    isSearchMode
                      ? "opacity-0 w-0 overflow-hidden md:opacity-100 md:w-auto md:overflow-visible"
                      : "opacity-100"
                  )}
                >
                  <Link
                    href="/feed"
                    className="font-display text-2xl font-bold tracking-[-0.04em] whitespace-nowrap text-white md:text-foreground"
                  >
                    Kraahl
                  </Link>
                </div>
                <div className="pointer-events-auto flex-1 md:relative md:flex md:h-10 md:justify-center">
                  {/* Mobile: collapsible search (unchanged). */}
                  <div className="md:hidden">
                    <AnimatedSearchBar
                      onSearch={handleSearch}
                      onSearchModeChange={setIsSearchMode}
                      overlayPaddingOffset={18}
                    />
                  </div>
                  {/* Desktop: persistent centered search field (mirrors the feed). */}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      submitDesktopSearch(brandingSearchValue);
                    }}
                    className="hidden h-11 w-full items-center gap-2.5 rounded-full border border-border/70 bg-secondary px-4 transition-colors focus-within:border-foreground/30 focus-within:ring-2 focus-within:ring-ring/30 md:flex md:max-w-[460px]"
                  >
                    <Search aria-hidden className="size-[18px] shrink-0 text-muted-foreground" />
                    <input
                      type="search"
                      value={brandingSearchValue}
                      onChange={(e) => setBrandingSearchValue(e.target.value)}
                      placeholder={tFeed("searchPlaceholder")}
                      aria-label={t("searchLabel")}
                      className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                    />
                  </form>
                </div>
                {/* Shared nav cluster — uniform with the feed header (cart incl.). */}
                <HeaderActions
                  isLoggedIn={!!currentUserId}
                  className="hidden shrink-0 md:flex"
                />
              </div>
              )}
            </div>
          ) : null}
          <div ref={subheaderRef} className="w-full min-w-0 leading-none">
            <div
              data-feed-tabs-host
              suppressHydrationWarning
              className={cn(
                "w-full min-w-0 leading-none",
                isProfilePage ? "transition-opacity duration-200 ease-out" : "transition-all duration-200 ease-out",
              )}
            >
              <div {...{ [PROFILE_HEADER_PORTAL_ATTR]: true }} suppressHydrationWarning />
              <div {...{ [HUB_HEADER_PORTAL_ATTR]: true }} suppressHydrationWarning />
            </div>
            <div
              data-feed-filters-host
              suppressHydrationWarning
              className="w-full min-w-0 transition-all duration-200 ease-out"
            />
          </div>
        </div>
      </header>
      )}
    </>
  );
}
